import type { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import type {
  SnakeCaseTrafficImpactMapping,
  TrafficImpactMapping
} from 'types/api/attacks/details/create'
import type {
  AttackCliArgs,
  AttackConfiguration,
  ContainerSelection,
  SnakeCaseAttackArgs
} from 'types/api/attacks/details/targeting'
import type { TaskStage } from 'types/api/attacks/details/task'
import type { RequestSource } from 'types/api/gamedays/gamedays'
import type { TriggerSourceKey } from 'types/api/runs-common'
import type { MultiValueTags, SingleValueTags } from 'types/tags'

export enum InfraAttackTarget {
  HOSTS = 'hosts',
  CONTAINERS = 'containers',
  KUBERNETES = 'kubernetes'
}
export enum StageLifecycle {
  Active = 'Active',
  Halting = 'Halting',
  Complete = 'Complete',
  Error = 'Error'
}

export interface ExecutionSummary {
  start_time: string
  end_time: string
  stage: TaskStage
  version: number
}

export enum TargetType {
  Host = 'Host',
  Container = 'Container',
  Kubernetes = 'Kubernetes'
}

enum K8sObjectKind {
  DEPLOYMENT = 'DEPLOYEMENT',
  REPLICASET = 'REPLICASET',
  POD = 'POD',
  SERVICE = 'SERVICE',
  STATEFULSET = 'STATEFULSET',
  DAEMONSET = 'DAEMONSET',
  INGRESS = 'INGRESS',
  DEPLOYMENTCONFIG = 'DEPLOYMENTCONFIG',
  REPLICATIONCONTROLLER = 'REPLICATIONCONTROLLER',
  ROLLOUT = 'ROLLOUT'
}

enum K8sObjectPodPhase {
  Pending = 'Pending',
  Running = 'Running',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
  Unknown = 'Unknown'
}

enum K8sDynamoContainerState {
  WAITING = 'WAITING',
  RUNNING = 'RUNNING',
  TERMINATED = 'TERMINATED',
  UNKNOWN = 'UNKNOWN'
}

export interface K8sContainerResponse {
  uid: string
  unified_uid: string
  name: string
  state: K8sDynamoContainerState
}

export interface K8sObjectResponse {
  cluster_id: string
  owner_reference: string
  created_at: string
  resolved_containers: string[]
  uid: string
  namespace: string
  name: string
  kind: K8sObjectKind
  labels: Record<string, string>
  annotations: Record<string, string>
  pod_phase: K8sObjectPodPhase
  host_id: string
  available_containers: K8sContainerResponse[]
  target_type: TargetType.Kubernetes
}

export enum K8sAttackStage {
  RESOLVING = 'RESOLVING',
  ERROR = 'ERROR',
  LAUNCHED = 'LAUNCHED',
  USER_HALTED = 'USER_HALTED'
}

export interface Strategy {
  allHosts?: boolean
  percentage?: number
  type: ProvenanceTargetType
  allContainers?: boolean
  tags?: SingleValueTags
  multiSelectTags?: MultiValueTags
  labels?: SingleValueTags
  multiSelectLabels?: MultiValueTags
}

export interface InfraTarget {
  resolvedHosts: string[]
  resolvedContainers?: string[]
  targetType: string
  strategy: Strategy
  strategyType: string
}

export interface ExecutionStageSummary {
  Successful: number
}

export interface InfraCommand {
  commandType: string
  commandArgs: SnakeCaseAttackArgs & {
    cliArgs?: AttackCliArgs
    providers?: string[]
    trafficImpactMapping?: TrafficImpactMapping
  }
}

export interface BaseInfraAttack {
  annotations: Record<string, string>
  args: AttackCliArgs
  attack_configuration: AttackConfiguration
  client_metrics_enabled_count: number
  command_template_id: string
  container_selection?: ContainerSelection
  create_source: RequestSource
  create_user: string
  created_at: string
  display_error_message: string
  dynamically_resolved_ips: string[]
  end_time: string
  execution_failure_reasons: Record<string, unknown>
  execution_stage_summary: Record<TaskStage, number>
  execution_summaries: Record<string, ExecutionSummary>
  guid: string
  include_new_clients: boolean
  infra_command: InfraCommand
  infra_target: InfraTarget
  initialization_end_time: string
  kind: RequestSource
  labels: Record<string, string>
  multiSelectLabels: MultiValueTags
  multiSelectTags: MultiValueTags
  org_id: string
  owning_team_id: string
  providers: string[]
  scenario_id: string
  scenario_node_guid: string
  scenario_run_number: number
  scenario_step_number: number
  service_id: string
  stage_lifecycle: StageLifecycle
  stage: TaskStage
  start_time: string
  tags: SingleValueTags
  target_containers: Record<string, string[]>
  target_template_id: string
  target_type: TargetType
  targets: string[]
  total_clients: number
  traffic_impact_mapping: SnakeCaseTrafficImpactMapping
  trigger_source?: TriggerSourceKey
  updated_at: string
}

export interface BaseK8sInfraAttack {
  k8s_attack_id: string
  k8s_object_response: K8sObjectResponse
  k8s_stage: K8sAttackStage
  k8s_attack_created_at: string
  k8s_attack_updated_at: string
  k8s_error: string
}

export interface InfraAttack extends BaseInfraAttack, BaseK8sInfraAttack {
  containerSelection?: ContainerSelection
  continue_on_empty_targets?: boolean
  continueOnEmptyTargets?: boolean
}
